/**
 * @file sheets/side-sheet/side-sheet-modal.tsx
 *
 * Modal Side Sheet — blocks background interaction with a Scrim overlay.
 *
 * Differences from `SideSheet` (standard):
 * - Uses `@radix-ui/react-dialog` for focus trap & scroll lock
 * - Portal rendering outside current DOM tree
 * - Scrim blocks background; clicking it calls `onClose`
 * - Escape key calls `onClose` (Radix built-in)
 * - `aria-modal="true"`
 *
 * @see https://m3.material.io/components/side-sheets/guidelines#modal
 */

import * as RadixDialog from "@radix-ui/react-dialog";
import { AnimatePresence, m } from "motion/react";
import * as React from "react";
import { cn } from "../../../lib/utils";
import { ScrollArea } from "../../scroll-area";
import { Scrim } from "../shared/scrim";
import type { BaseSheetProps, SheetEdge } from "../shared/types";
import { buildSideSheetAnim, resolveDividers } from "../shared/utils";

// ─── Types ────────────────────────────────────────────────────────────────────

/**
 * Props for the modal Side Sheet.
 */
export interface SideSheetModalProps extends BaseSheetProps {
	/**
	 * Which edge the sheet slides in from.
	 * @default "end"
	 */
	edge?: SheetEdge;

	/**
	 * Width of the sheet.
	 * @default "360px"
	 */
	width?: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Modal Side Sheet that slides in from a screen edge and blocks background interaction.
 *
 * Dismiss by: clicking the Scrim or pressing Escape.
 *
 * @example
 * ```tsx
 * <SideSheetModal isOpen={isOpen} onClose={() => setIsOpen(false)} edge="end" aria-labelledby="panel-title">
 *   <h2 id="panel-title">Panel</h2>
 *   <p>Modal side content</p>
 * </SideSheetModal>
 * ```
 */
export const SideSheetModal = React.forwardRef<
	HTMLDivElement,
	SideSheetModalProps
>(
	(
		{
			isOpen,
			onClose,
			children,
			className,
			edge = "end",
			width = "360px",
			"aria-labelledby": ariaLabelledBy,
			"aria-label": ariaLabel,
			header,
			footer,
			divider,
			scrollAreaProps,
		},
		ref,
	) => {
		const sheetAnim = React.useMemo(() => buildSideSheetAnim(edge), [edge]);
		const resolvedDividers = React.useMemo(
			() => resolveDividers(divider),
			[divider],
		);

		return (
			<RadixDialog.Root
				open={isOpen}
				onOpenChange={(open) => !open && onClose()}
			>
				<RadixDialog.Portal forceMount>
					<AnimatePresence mode="wait">
						{isOpen && (
							<>
								<RadixDialog.Overlay asChild>
									<Scrim onClick={onClose} />
								</RadixDialog.Overlay>

								<RadixDialog.Content
									asChild
									aria-describedby={undefined}
									aria-labelledby={ariaLabelledBy}
									aria-label={ariaLabel}
									onEscapeKeyDown={onClose}
								>
									<m.div
										ref={ref}
										className={cn(
											"fixed top-0 bottom-0 z-50",
											edge === "end" ? "right-0" : "left-0",
											"bg-m3-surface-container-low",
											"flex flex-col overflow-hidden",
											edge === "end"
												? "border-l border-m3-outline-variant rounded-l-m3-xl"
												: "border-r border-m3-outline-variant rounded-r-m3-xl",
											"outline-none focus-visible:ring-2 focus-visible:ring-m3-primary focus-visible:ring-inset",
											className,
										)}
										style={{ width }}
										{...sheetAnim}
									>
										<RadixDialog.Title className="sr-only">
											{ariaLabel || "Side Sheet"}
										</RadixDialog.Title>

										{header && (
											<div className="shrink-0 px-6 py-4">{header}</div>
										)}
										{header && resolvedDividers.header && (
											<hr className="border-t border-m3-outline-variant w-full shrink-0 m-0" />
										)}

										<ScrollArea
											type="hover"
											{...scrollAreaProps}
											className={cn(
												"flex-1 min-h-0 min-w-0",
												scrollAreaProps?.className,
											)}
											viewportClassName={cn(
												"overscroll-contain",
												scrollAreaProps?.viewportClassName,
											)}
											viewportProps={scrollAreaProps?.viewportProps}
										>
											{children}
										</ScrollArea>

										{footer && resolvedDividers.footer && (
											<hr className="border-t border-m3-outline-variant w-full shrink-0 m-0" />
										)}
										{footer && (
											<div className="shrink-0 px-6 py-4">{footer}</div>
										)}
									</m.div>
								</RadixDialog.Content>
							</>
						)}
					</AnimatePresence>
				</RadixDialog.Portal>
			</RadixDialog.Root>
		);
	},
);

SideSheetModal.displayName = "SideSheetModal";
